import { shouldCheckpoint, } from '@orbit-mind/editor-commands';
import { appError, err, ok, } from '@orbit-mind/shared-types';
import { storageFailure } from './errors';
import { createWriteQueue } from './queue';
export const DEFAULT_JOURNAL_POLICY = {
    checkpointEveryEntries: 50,
    checkpointEveryMs: (5 * 60_000),
    recentOperationWindow: 10_000,
};
export function createJournalStore(input) {
    const { storage, mapId } = input;
    const policy = input.policy ?? DEFAULT_JOURNAL_POLICY;
    const queue = createWriteQueue();
    let lastSequence = input.state.lastSequence;
    let checkpointSequence = input.state.checkpointSequence;
    let checkpointAt = input.state.checkpointAt;
    let pendingOperations = input.state.pendingOperations;
    let recoveryAvailable = input.state.recoveryAvailable;
    // Dismissing an alert never grants permission to overwrite the only recoverable journal.
    const readOnlyRecovery = input.state.recoveryAvailable;
    const recoveryError = () => appError('MAP_CORRUPTED', 'Create a recovered copy before editing this map. The original is preserved.', { retryable: false });
    /**
     * When the stretch since the last checkpoint began.
     *
     * Taken from the first command appended after a checkpoint rather than from a clock: the
     * command's own timestamp is already the moment the user made the edit, and using it keeps
     * this store free of a clock it would otherwise have to be handed.
     */
    let sinceCheckpointStartedAt = null;
    /**
     * Counted separately from the queue's own size, which also carries reads.
     *
     * `localWriteInFlight` is the difference between "saving" and "saved locally", and reading the
     * outbox is not a reason to tell the user their work might not be on disk.
     */
    let writesInFlight = 0;
    // Insertion-ordered and trimmed from the front, so this is a FIFO window rather than a set
    // that grows for as long as the tab stays open.
    const recentOperations = new Set(input.state.pendingOperationIds);
    const listeners = new Set();
    function status() {
        return {
            mapId,
            lastSequence,
            checkpointSequence,
            checkpointAt,
            pendingOperations,
            localWriteInFlight: writesInFlight > 0,
            recoveryAvailable,
        };
    }
    function notify() {
        const snapshot = status();
        for (const listener of listeners)
            listener(snapshot);
    }
    /**
     * Queues a mutation and holds `localWriteInFlight` for as long as it is outstanding.
     *
     * The count is raised before the task is queued, not when it starts, so a UI watching the
     * status shows "saving" from the moment the edit was handed over rather than from whenever the
     * queue reaches it. Those are different moments during a burst of typing, and the gap between
     * them is precisely when a crash would lose the edit.
     */
    function runWrite(task) {
        writesInFlight += 1;
        notify();
        return queue.run(task).finally(() => {
            writesInFlight -= 1;
            notify();
        });
    }
    function remember(operationId) {
        recentOperations.add(operationId);
        while (recentOperations.size > policy.recentOperationWindow) {
            const oldest = recentOperations.values().next();
            if (oldest.done === true)
                break;
            recentOperations.delete(oldest.value);
        }
    }
    function appendAll(commands) {
        if (readOnlyRecovery)
            return Promise.resolve(err(recoveryError()));
        return runWrite(async () => {
            const fresh = [];
            const duplicates = [];
            for (const command of commands) {
                if (recentOperations.has(command.meta.operationId)) {
                    duplicates.push(command.meta.operationId);
                    continue;
                }
                fresh.push(command);
            }
            if (fresh.length === 0)
                return ok({ entries: [], duplicates });
            const records = fresh.map((command, index) => ({
                mapId,
                sequence: lastSequence + index + 1,
                operationId: command.meta.operationId,
                command,
                synced: false,
            }));
            try {
                await storage.appendEntries(records);
            }
            catch (cause) {
                // `lastSequence` is deliberately untouched. Nothing was written, so those numbers were
                // never used, and a retry that claims them again is correct rather than a collision.
                return err(storageFailure('append', cause));
            }
            lastSequence += records.length;
            pendingOperations += records.length;
            for (const record of records)
                remember(record.operationId);
            const first = fresh[0];
            if (sinceCheckpointStartedAt === null && first !== undefined) {
                sinceCheckpointStartedAt = first.meta.timestamp;
            }
            return ok({ entries: records.map(toEntry), duplicates });
        });
    }
    function writeCheckpoint(document, at) {
        if (readOnlyRecovery)
            return Promise.resolve(err(recoveryError()));
        return runWrite(async () => {
            // `lastSequence` is read inside the queued task, not before it: an append that was already
            // waiting has to be counted, or the checkpoint would claim a sequence it does not cover
            // and that command would replay a second time over a document that already contains it.
            const record = { mapId, sequence: lastSequence, document, createdAt: at };
            try {
                await storage.writeCheckpoint(record);
            }
            catch (cause) {
                return err(storageFailure('checkpoint', cause));
            }
            checkpointSequence = record.sequence;
            checkpointAt = at;
            sinceCheckpointStartedAt = null;
            return ok(toCheckpoint(record));
        });
    }
    return {
        mapId,
        append: (command) => appendAll([command]),
        appendAll,
        pending() {
            if (readOnlyRecovery)
                return Promise.resolve(err(recoveryError()));
            // Queued so the outbox reflects every append already handed over rather than racing one.
            return queue.run(async () => {
                try {
                    const read = await storage.readPending(mapId);
                    // Damaged rows are reported at open, where there is a recovery screen to report them
                    // to. Here the caller is the sync layer, and an entry it cannot read is one it cannot
                    // send — leaving it out of the outbox is the only thing it can do with it.
                    return ok(read.records.map(toEntry));
                }
                catch (cause) {
                    return err(storageFailure('read pending', cause));
                }
            });
        },
        markSynced(operationIds) {
            if (operationIds.length === 0)
                return Promise.resolve(ok(0));
            return runWrite(async () => {
                const wanted = new Set(operationIds);
                let cleared;
                try {
                    const read = await storage.readPending(mapId);
                    const sequences = read.records
                        .filter((record) => wanted.has(record.operationId))
                        .map((record) => record.sequence);
                    if (sequences.length > 0)
                        await storage.markSynced(mapId, sequences);
                    cleared = sequences.length;
                }
                catch (cause) {
                    return err(storageFailure('mark synced', cause));
                }
                pendingOperations = Math.max(0, pendingOperations - cleared);
                return ok(cleared);
            });
        },
        maybeCheckpoint(document, at) {
            const due = shouldCheckpoint({
                entriesSinceCheckpoint: lastSequence - checkpointSequence,
                // With no checkpoint yet, the stretch is measured from the first edit of the session.
                // Falling back to `at` instead would make the elapsed time zero and the time-based rule
                // unreachable, so a slow session would never checkpoint at all.
                msSinceLastCheckpoint: at - (checkpointAt ?? sinceCheckpointStartedAt ?? at),
                everyEntries: policy.checkpointEveryEntries,
                everyMs: policy.checkpointEveryMs,
            });
            if (!due)
                return Promise.resolve(ok(null));
            return writeCheckpoint(document, at);
        },
        checkpoint: writeCheckpoint,
        status,
        subscribe(listener) {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
        dismissRecovery() {
            if (!recoveryAvailable)
                return;
            recoveryAvailable = false;
            notify();
        },
        flush: () => queue.idle(),
    };
}
/** The storage shape reduced to what replay and the outbox actually read. */
export function toEntry(record) {
    return { command: record.command, sequence: record.sequence, synced: record.synced };
}
export function toCheckpoint(record) {
    return { document: record.document, sequence: record.sequence, createdAt: record.createdAt };
}
//# sourceMappingURL=store.js.map